import { useTranslation } from "react-i18next";
import { useDecision } from "@optimizely/react-sdk";
import { RowInfo, FormAutocomplete, ChainSelect, Loadable, HelpModal } from "../../common";
import { useContext, useMemo } from "react";
import { getRadiusLabel, radiusOptionsNoUnits } from "../../consts/settings";
import { OrganizeContext } from '../../organize/Organize.context'
import { HelpText } from '../../App.styles'

const LocksSection = ({ useQuery }) => {
  const { t } = useTranslation();
  const [{ id, latitude, longitude }] = useQuery();
  const { challenges: challengesContext } = useContext(OrganizeContext);
  const { tasks: challenges = [], isLoading } = challengesContext || {};
  const [decisionNpcLocation] = useDecision('npc-location');
  const filteredChallenges = [
    {
      id: "0",
      name: "",
    },
  ].concat(challenges.filter((challenge) => challenge.id !== id));
  const disabled = !longitude && !latitude;
  const chainKeys = useMemo(() => {
    const chains = filteredChallenges
        .map((c) => {
            return `${c?.name || ""}-${c?.task_type || ""}`;
        })
        .join("/");
    // Obfuscate chain keys for unique ids and prevent unnecessary re-render
    try {
        return window.btoa(encodeURI(chains));
    } catch {
        // Fallback when obfuscating chain keys failed
        return JSON.stringify(chains);
    }
  }, [filteredChallenges]);
  return (
    <>
        <HelpText className="position-relative top-0 d-block mb-4" focus>
            <p>{t(`You can lock a NPC such that it requires another challenge, a set of challenges, 
            or even particular answers from certain challenges.`)}</p>
            <p>{t(`You can lock the NPC to require the player to being within a small radius of the 
            location, in the real world, that you set (Must select a location before using this feature).`)}</p>
        </HelpText>
        <RowInfo help={(
            <HelpModal title={t('Lock by Challenge')}>
                <p>
                    {t(`Select the challenges, and particular answers for some challenge types, 
                    that you will require the player to complete before being able to unlock and 
                    interact with this NPC.`)}
                </p>
                <ul>
                    <li>
                        {t('You can select 1 or many challenges.')}
                    </li>
                    <li>
                        {t(`This allows you to expose different NPC characters based on the choices (split path) 
                        or successes (correctness) of the players.`)}
                    </li>
                </ul>
            </HelpModal>
        )}>
            <Loadable loading={isLoading}>
                <ChainSelect
                    help={t("Locking is optional.")}
                    label={t("Unlock by Completing Challenge")}
                    name="unlock_by_task_ids"
                    useQuery={useQuery}
                    options={filteredChallenges}
                    size="small"
                    childKeys={{
                        5: "trivia",
                        6: "multiplechoice",
                        11: "multiplechoice",
                    }}
                    key={chainKeys}
                />
            </Loadable>
        </RowInfo>
        {
            decisionNpcLocation?.enabled
                ? (
                    <RowInfo help={(
                        <HelpModal title={t("Lock By")}>
                            <p>{t(`You can lock the NPC to require the player to being within a small 
                            radius of the location, in the real world, that you set (Must select a 
                            location before using this feature).`)}</p>
                        </HelpModal>
                    )}>
                        <FormAutocomplete
                            desc={
                                disabled ? t("Location must be enabled to use this feature") : null
                            }
                            label={t("Unlock when closer than:")}
                            name="unlock_by_location_radius"
                            useQuery={useQuery}
                            disabled={disabled}
                            options={radiusOptionsNoUnits}
                            size="small"
                            renderLabel={getRadiusLabel}
                            creatable
                        />
                    </RowInfo>
                )
                : null
        }
    </>
  );
};

export default LocksSection;
